import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { differenceInMinutes, parseISO } from 'date-fns';

import { Mitarbeiter, Projekt, Zeiteintrag, ZeiteintragEingabe } from '../../types/zeiterfassung';
import { alsApiDatum, alsApiZeit, formatStunden, zeitAlsDate } from '../../utils/format';

type Props = {
  offen: boolean;
  eintrag: Zeiteintrag | null;
  projekte: Projekt[];
  projekteLaden: boolean;
  mitarbeiter: Mitarbeiter[];
  darfMitarbeiterAendern: boolean;
  onAbbrechen: () => void;
  onSpeichern: (eingabe: ZeiteintragEingabe) => Promise<boolean>;
};

const ZeiteintragDialog = ({
  offen,
  eintrag,
  projekte,
  projekteLaden,
  mitarbeiter,
  darfMitarbeiterAendern,
  onAbbrechen,
  onSpeichern
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [datum, setDatum] = useState<Date | null>(null);
  const [projektId, setProjektId] = useState('');
  const [startZeit, setStartZeit] = useState<Date | null>(null);
  const [endZeit, setEndZeit] = useState<Date | null>(null);
  const [pause, setPause] = useState(0);
  const [beschreibung, setBeschreibung] = useState('');
  const [mitarbeiterId, setMitarbeiterId] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [abgeschickt, setAbgeschickt] = useState(false);

  // Alle Felder beim Wechsel des Eintrags neu setzen.
  //
  // Vorher fehlte hier der Mitarbeiter: beim Bearbeiten mehrerer Einträge
  // hintereinander blieb die Auswahl des vorherigen Eintrags stehen.
  useEffect(() => {
    if (!eintrag) return;

    setDatum(parseISO(eintrag.date));
    setProjektId(String(eintrag.projectId));
    setStartZeit(zeitAlsDate(eintrag.startTime));
    setEndZeit(zeitAlsDate(eintrag.endTime));
    setPause(eintrag.breakDuration || 0);
    setBeschreibung(eintrag.description || '');
    setMitarbeiterId(String(eintrag.userId));
    setAbgeschickt(false);
  }, [eintrag]);

  const stunden = useMemo(() => {
    if (!startZeit || !endZeit) return 0;
    const minuten = differenceInMinutes(endZeit, startZeit);
    if (minuten <= 0) return 0;
    return Math.max(0, Math.round(((minuten - pause) / 60) * 100) / 100);
  }, [startZeit, endZeit, pause]);

  const fehler = useMemo(() => {
    const f: Record<string, string> = {};

    if (!datum) f.datum = 'Bitte ein Datum wählen';
    if (!projektId) f.projekt = 'Bitte ein Projekt wählen';
    if (!startZeit) f.start = 'Bitte eine Startzeit angeben';
    if (!endZeit) f.ende = 'Bitte eine Endzeit angeben';

    if (startZeit && endZeit && differenceInMinutes(endZeit, startZeit) <= 0) {
      f.ende = 'Die Endzeit muss nach der Startzeit liegen';
    }

    if (startZeit && endZeit && pause >= differenceInMinutes(endZeit, startZeit)) {
      f.pause = 'Die Pause ist länger als die Zeitspanne';
    }

    return f;
  }, [datum, projektId, startZeit, endZeit, pause]);

  const istGueltig = Object.keys(fehler).length === 0;
  const zeigeFehler = (feld: string) => (abgeschickt ? fehler[feld] : undefined);

  const absenden = async () => {
    setAbgeschickt(true);
    if (!istGueltig) return;

    const apiDatum = alsApiDatum(datum);
    const apiStart = alsApiZeit(startZeit);
    const apiEnde = alsApiZeit(endZeit);

    if (!apiDatum || !apiStart || !apiEnde) return;

    setSpeichert(true);
    try {
      const erfolgreich = await onSpeichern({
        date: apiDatum,
        projectId: Number(projektId),
        startTime: apiStart,
        endTime: apiEnde,
        breakDuration: pause,
        description: beschreibung,
        // Wichtig: Das Backend erwartet `forUserId`. Vorher wurde `userId`
        // gesendet und der Mitarbeiterwechsel deshalb stillschweigend ignoriert.
        ...(darfMitarbeiterAendern && mitarbeiterId
          ? { forUserId: Number(mitarbeiterId) }
          : {})
      });

      if (erfolgreich) {
        onAbbrechen();
      }
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <Dialog open={offen} onClose={onAbbrechen} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle>Zeiteintrag bearbeiten</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {darfMitarbeiterAendern && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="dialog-mitarbeiter-label">Mitarbeiter</InputLabel>
                <Select
                  labelId="dialog-mitarbeiter-label"
                  value={mitarbeiterId}
                  onChange={(e: SelectChangeEvent) => setMitarbeiterId(e.target.value)}
                  label="Mitarbeiter"
                >
                  {mitarbeiter.map(m => (
                    <MenuItem key={m.id} value={String(m.id)}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Datum"
              value={datum}
              onChange={setDatum}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: Boolean(zeigeFehler('datum')),
                  helperText: zeigeFehler('datum')
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={Boolean(zeigeFehler('projekt'))}>
              <InputLabel id="dialog-projekt-label">Projekt</InputLabel>
              <Select
                labelId="dialog-projekt-label"
                value={projektId}
                label="Projekt"
                onChange={(e: SelectChangeEvent) => setProjektId(e.target.value)}
                disabled={projekteLaden}
              >
                {projekte.map(projekt => (
                  <MenuItem key={projekt.id} value={String(projekt.id)}>
                    {projekt.name}
                  </MenuItem>
                ))}
              </Select>
              {zeigeFehler('projekt') && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {zeigeFehler('projekt')}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={6}>
            <TimePicker
              label="Startzeit"
              value={startZeit}
              onChange={setStartZeit}
              ampm={false}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: Boolean(zeigeFehler('start')),
                  helperText: zeigeFehler('start')
                }
              }}
            />
          </Grid>

          <Grid item xs={6} sm={6}>
            <TimePicker
              label="Endzeit"
              value={endZeit}
              onChange={setEndZeit}
              ampm={false}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: Boolean(zeigeFehler('ende')),
                  helperText: zeigeFehler('ende')
                }
              }}
            />
          </Grid>

          <Grid item xs={6} sm={6}>
            <TextField
              fullWidth
              label="Pause (Minuten)"
              type="number"
              value={pause}
              onChange={e => setPause(Math.max(0, parseInt(e.target.value, 10) || 0))}
              inputProps={{ min: 0, step: 5 }}
              error={Boolean(zeigeFehler('pause'))}
              helperText={zeigeFehler('pause')}
            />
          </Grid>

          <Grid item xs={6} sm={6}>
            <TextField
              fullWidth
              label="Ergibt"
              value={formatStunden(stunden)}
              InputProps={{ readOnly: true }}
              helperText="Wird automatisch berechnet"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Beschreibung"
              value={beschreibung}
              onChange={e => setBeschreibung(e.target.value)}
              multiline
              rows={2}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onAbbrechen} disabled={speichert}>
          Abbrechen
        </Button>
        <Button
          onClick={absenden}
          variant="contained"
          disabled={speichert}
          startIcon={speichert ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ZeiteintragDialog;
